/**
 * SNAP SOLVES — Custom Interactions & Animations
 * Modern UX inspired by Framer, Dribbble, and motion design
 */

(function () {
  'use strict';

  /* ==========================================
     LOADING SCREEN
     ========================================== */
  function initLoader() {
    // Keep the loader permanently hidden — navigation no longer uses an overlay.
    // In-page image skeletons handle the loading state instead.
    const loader = document.getElementById('snap-loader');
    if (!loader) return;
    function hide() {
      loader.style.cssText = 'opacity:0;visibility:hidden;pointer-events:none';
    }
    hide();
    window.addEventListener('pageshow', hide);
  }

  /* ==========================================
     PRODUCT CARD SKELETONS
     Skeleton HTML is server-rendered in card-product.liquid so it exists
     from first paint — no JS injection, no flicker.
     JS only removes it once the image has loaded.
     ========================================== */
  function initImageSkeletons() {
    function revealCard(wrapper) {
      const sk = wrapper.querySelector('.snap-card-skeleton');
      if (!sk) return;
      wrapper.classList.remove('snap-card-loading');
      sk.classList.add('snap-card-skeleton--done');
      sk.addEventListener('transitionend', () => sk.remove(), { once: true });
    }

    function initCard(wrapper) {
      if (wrapper.dataset.snapSk) return;
      wrapper.dataset.snapSk = '1';

      const img = wrapper.querySelector('.card__media img');
      if (!img) { revealCard(wrapper); return; }

      if (img.complete && img.naturalWidth > 0) {
        revealCard(wrapper);
      } else {
        img.addEventListener('load',  () => revealCard(wrapper), { once: true });
        img.addEventListener('error', () => revealCard(wrapper), { once: true });
      }
    }

    document.querySelectorAll('.card-wrapper').forEach(initCard);

    // Cards added dynamically (quick-add modals, upsells, etc.)
    new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('.card-wrapper')) initCard(node);
          node.querySelectorAll && node.querySelectorAll('.card-wrapper').forEach(initCard);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ==========================================
     CUSTOM CURSOR
     ========================================== */
  function initCursor() {
    if (window.matchMedia('(max-width: 768px)').matches) return;
    const cursor = document.getElementById('snap-cursor');
    const ring = document.getElementById('snap-cursor-ring');
    if (!cursor || !ring) return;

    let cx = window.innerWidth / 2,
      cy = window.innerHeight / 2;
    let rx = cx,
      ry = cy;
    let raf = null;

    function animate() {
      cursor.style.transform = `translate(${cx}px, ${cy}px)`;
      rx += (cx - rx) * 0.12;
      ry += (cy - ry) * 0.12;
      ring.style.transform = `translate(${rx}px, ${ry}px)`;
      // Keep looping only while ring is still catching up; stop when idle
      if (Math.abs(rx - cx) > 0.1 || Math.abs(ry - cy) > 0.1) {
        raf = requestAnimationFrame(animate);
      } else {
        raf = null;
      }
    }

    document.addEventListener('mousemove', (e) => {
      cx = e.clientX;
      cy = e.clientY;
      if (!raf) raf = requestAnimationFrame(animate);
    });

    // Hide when leaving window
    document.addEventListener('mouseleave', () => {
      cursor.style.opacity = '0';
      ring.style.opacity = '0';
    });
    document.addEventListener('mouseenter', () => {
      cursor.style.opacity = '1';
      ring.style.opacity = '1';
    });

    // Expand on interactive elements
    const interactables =
      'a, button, [role="button"], input, select, textarea, label, .card-wrapper';
    document.addEventListener('mouseover', (e) => {
      if (e.target.closest(interactables)) {
        cursor.classList.add('snap-cursor--hover');
        ring.classList.add('snap-cursor--hover');
      }
    });
    document.addEventListener('mouseout', (e) => {
      if (e.target.closest(interactables)) {
        cursor.classList.remove('snap-cursor--hover');
        ring.classList.remove('snap-cursor--hover');
      }
    });
  }

  /* ==========================================
     SCROLL ANIMATIONS (Intersection Observer)
     ========================================== */
  function initScrollAnimations() {
    const targets = document.querySelectorAll(
      '.card-wrapper, .snap-feature, .snap-hero__text, .snap-hero__visual, ' +
        '.snap-fade-up, .snap-fade-in, .snap-scale-in, ' +
        '.featured-collection, .collection-list, ' +
        '.title:not(.snap-hero__title), .subtitle',
    );

    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            // Stagger children
            const el = entry.target;
            const delay = el.dataset.snapDelay || 0;
            setTimeout(() => {
              el.classList.add('snap-visible');
            }, parseInt(delay));
            observer.unobserve(el);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -60px 0px',
      },
    );

    targets.forEach((el, i) => {
      // Add animation class if not already set
      if (
        !el.classList.contains('snap-fade-up') &&
        !el.classList.contains('snap-fade-in') &&
        !el.classList.contains('snap-scale-in')
      ) {
        el.classList.add('snap-fade-up');
      }
      // Stagger grid items
      if (el.classList.contains('card-wrapper')) {
        const siblings = el.parentElement.querySelectorAll('.card-wrapper');
        const idx = Array.from(siblings).indexOf(el);
        el.dataset.snapDelay = Math.min(idx * 80, 400);
      }
      observer.observe(el);
    });
  }

  /* ==========================================
     TYPEWRITER EFFECT
     ========================================== */
  function initTypewriter() {
    const el = document.getElementById('snap-typewriter');
    if (!el) return;

    const words = ['Inspire', 'Print', 'Sell', 'Create', 'Impress'];
    let wordIdx = 0;
    let charIdx = 0;
    let deleting = false;
    let paused = false;

    function type() {
      const word = words[wordIdx];

      if (!deleting) {
        el.textContent = word.slice(0, charIdx + 1);
        charIdx++;
        if (charIdx === word.length) {
          paused = true;
          setTimeout(() => {
            paused = false;
            deleting = true;
            setTimeout(type, 80);
          }, 1800);
          return;
        }
      } else {
        el.textContent = word.slice(0, charIdx - 1);
        charIdx--;
        if (charIdx === 0) {
          deleting = false;
          wordIdx = (wordIdx + 1) % words.length;
        }
      }
      setTimeout(type, deleting ? 60 : 110);
    }
    setTimeout(type, 1200);
  }

  /* ==========================================
     CARD TILT EFFECT (3D perspective)
     ========================================== */
  function initCardTilt() {
    if (window.matchMedia('(max-width: 990px)').matches) return;

    const cards = document.querySelectorAll('.card-wrapper');
    cards.forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        card.style.transform = `
          perspective(800px)
          rotateY(${x * 8}deg)
          rotateX(${-y * 8}deg)
          translateY(-8px)
          scale(1.02)
        `;
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = '';
        card.style.transition = 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)';
        setTimeout(() => (card.style.transition = ''), 500);
      });
    });
  }

  /* ==========================================
     MAGNETIC BUTTONS
     ========================================== */
  function initMagneticButtons() {
    if (window.matchMedia('(max-width: 990px)').matches) return;

    const btns = document.querySelectorAll(
      '.snap-btn--primary, .snap-btn--ghost',
    );
    btns.forEach((btn) => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) * 0.3;
        const y = (e.clientY - rect.top - rect.height / 2) * 0.3;
        btn.style.transform = `translate(${x}px, ${y}px) scale(1.03)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  }

  /* ==========================================
     SMOOTH HEADER TRANSPARENCY
     ========================================== */
  function initHeaderScroll() {
    const header = document.querySelector('.section-header');
    if (!header) return;

    const updateHeader = () => {
      const scrolled = window.scrollY > 60;
      header.classList.toggle('snap-header--scrolled', scrolled);
    };

    window.addEventListener('scroll', updateHeader, { passive: true });
    updateHeader();
  }

  /* ==========================================
     PARALLAX ON HERO GLOWS
     ========================================== */
  function initHeroParallax() {
    const glows = document.querySelectorAll('.snap-hero__glow');
    const visual = document.querySelector('.snap-hero__visual');
    if (!glows.length) return;

    window.addEventListener('mousemove', (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;

      glows.forEach((glow, i) => {
        const factor = (i + 1) * 12;
        glow.style.transform = `translate(${x * factor}px, ${y * factor}px)`;
      });

      if (visual) {
        visual.style.transform = `translate(${x * 8}px, ${y * 8}px)`;
      }
    });
  }

  /* ==========================================
     MARQUEE DUPLICATION
     ========================================== */
  function initMarquee() {
    const track = document.querySelector('.snap-marquee__track');
    if (!track) return;
    // Duplicate content for seamless loop
    track.innerHTML += track.innerHTML;
  }

  /* ==========================================
     PRODUCT IMAGE ZOOM ON HOVER (collection)
     ========================================== */
  function initImageHover() {
    const mediaWrappers = document.querySelectorAll('.card__media');
    mediaWrappers.forEach((wrapper) => {
      const img = wrapper.querySelector('img');
      if (!img) return;
      wrapper.addEventListener('mouseenter', () => {
        img.style.transition = 'transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)';
        img.style.transform = 'scale(1.08)';
      });
      wrapper.addEventListener('mouseleave', () => {
        img.style.transform = 'scale(1)';
      });
    });
  }

  /* ==========================================
     COUNTER ANIMATION
     ========================================== */
  function animateCounters() {
    const counters = document.querySelectorAll('.snap-hero__stat-number');
    if (!counters.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const text = el.textContent;
          const num = parseFloat(text.replace(/[^0-9.]/g, ''));
          const suffix = text.replace(/[0-9.]/g, '');

          if (isNaN(num)) return;

          let start = 0;
          const duration = 1200;
          const step = (timestamp) => {
            if (!start) start = timestamp;
            const progress = Math.min((timestamp - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 4); // ease-out-quart
            el.textContent = Math.floor(eased * num) + suffix;
            if (progress < 1) requestAnimationFrame(step);
            else el.textContent = text;
          };
          requestAnimationFrame(step);
          observer.unobserve(el);
        });
      },
      { threshold: 0.5 },
    );

    counters.forEach((c) => observer.observe(c));
  }

  /* ==========================================
     GRID HOVER GLOW (follows mouse)
     ========================================== */
  function initGridGlow() {
    const grids = document.querySelectorAll('.product-grid, .grid');
    grids.forEach((grid) => {
      grid.addEventListener('mousemove', (e) => {
        const cards = grid.querySelectorAll('.card');
        cards.forEach((card) => {
          const rect = card.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          card.style.setProperty('--mouse-x', `${x}px`);
          card.style.setProperty('--mouse-y', `${y}px`);
        });
      });
    });
  }

  /* ==========================================
     ANNOUNCEMENT BAR TICKER
     ========================================== */
  function initAnnouncementBar() {
    const bar = document.querySelector('.announcement-bar');
    const msg = bar?.querySelector('.announcement-bar__message');
    if (!bar || !msg) return;

    const track = document.createElement('div');
    track.className = 'snap-ann-track';
    bar.insertBefore(track, msg);
    track.appendChild(msg);

    // Add enough copies to fill double the screen width for seamless looping
    const copies =
      Math.ceil((window.innerWidth * 2) / (msg.offsetWidth || 300)) + 2;
    for (let i = 0; i < copies; i++) {
      track.appendChild(msg.cloneNode(true));
    }

    // Animate by the width of one copy for seamless loop
    const oneWidth = msg.offsetWidth;
    const totalWidth = track.scrollWidth;
    const ratio = ((oneWidth / totalWidth) * 100).toFixed(4);
    const style = document.createElement('style');
    style.textContent = `@keyframes snap-ann-scroll { from { transform: translateX(0); } to { transform: translateX(-${ratio}%); } }`;
    document.head.appendChild(style);
  }

  /* ==========================================
     SIDE NAV PANEL
     ========================================== */
  function initSideNavActive() {
    const panel = document.getElementById('snap-sidenav');
    if (!panel) return;

    const currentPath = window.location.pathname.replace(/\/$/, '') || '/';

    // Mark active top-level links
    panel.querySelectorAll('.snap-sidenav__link').forEach((link) => {
      const href = (link.getAttribute('href') || '').split('?')[0].replace(/\/$/, '');
      if (!href || href === '#') return;

      const isActive = currentPath === href || (href !== '/' && currentPath.startsWith(href + '/')) || (href !== '/' && currentPath.startsWith(href));
      if (isActive) link.classList.add('snap-active');
    });

    // Mark active sub-links and open their parent accordion
    panel.querySelectorAll('.snap-sidenav__sub-link').forEach((link) => {
      const href = (link.getAttribute('href') || '').split('?')[0].replace(/\/$/, '');
      if (!href || href === '#') return;

      const isActive = currentPath === href || currentPath.startsWith(href + '/') || currentPath.startsWith(href);
      if (!isActive) return;

      link.classList.add('snap-active');

      const accordion = link.closest('.snap-sidenav__accordion');
      if (accordion) {
        accordion.setAttribute('open', '');
        const summary = accordion.querySelector('summary');
        if (summary) summary.classList.add('snap-active');
      }
    });
  }

  function initSideNav() {
    const panel = document.getElementById('snap-sidenav');
    const overlay = document.getElementById('snap-sidenav-overlay');
    const closeBtn = document.getElementById('snap-sidenav-close');
    // Use the header's existing burger button as the trigger
    const burger = document.querySelector('.header__icon--summary');
    if (!panel || !overlay) return;

    function openPanel() {
      panel.classList.add('snap-open');
      overlay.classList.add('snap-active');
      document.body.classList.add('snap-nav-open');
      panel.removeAttribute('inert');
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }

    function closePanel() {
      panel.classList.remove('snap-open');
      overlay.classList.remove('snap-active');
      document.body.classList.remove('snap-nav-open');
      panel.setAttribute('inert', '');
      document.body.style.overflow = '';
    }

    if (burger) {
      burger.addEventListener(
        'click',
        (e) => {
          // Prevent the theme's built-in drawer from opening
          e.preventDefault();
          e.stopImmediatePropagation();
          openPanel();
        },
        true,
      );
    }

    overlay.addEventListener('click', closePanel);
    if (closeBtn) closeBtn.addEventListener('click', closePanel);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closePanel();
    });

    // Click outside — close if click lands outside the panel
    document.addEventListener('click', (e) => {
      if (!panel.classList.contains('snap-open')) return;
      if (!panel.contains(e.target) && e.target !== overlay) closePanel();
    });
  }

  /* ==========================================
     INLINE SEARCH — filter existing Liquid cards in place
     Supports multiple .snap-collection-search forms on a page
     ========================================== */
  function initOneInlineSearch(form) {
    var input = form.querySelector('.snap-search-input');
    var closeBtn = form.querySelector('.snap-search-close');
    var icon = form.querySelector('.snap-search-icon');
    var label = form.querySelector('.snap-search-label');
    if (!input) return;

    // Find the nearest product grid — works for both homepage featured-collection
    // and collection page (section tag or wrapping div)
    var container = form.closest('section, .featured-collection, .product-grid-container, [id="ProductGridContainer"]')
      || form.parentElement;
    var grid = container && container.querySelector('ul.product-grid');
    // Last-resort fallback: closest ul.product-grid anywhere on the page
    if (!grid) grid = document.querySelector('ul.product-grid');

    var searchTimer;
    var noResultsEl = null;

    function getCards() {
      return grid ? Array.prototype.slice.call(grid.querySelectorAll('li.grid__item')) : [];
    }

    function filterCards(query) {
      if (!grid) return;
      var q = query.trim().toLowerCase();

      if (noResultsEl && noResultsEl.parentNode) {
        noResultsEl.parentNode.removeChild(noResultsEl);
        noResultsEl = null;
      }

      var cards = getCards();
      var anyVisible = false;

      cards.forEach(function(card) {
        var titleEl = card.querySelector('.card__heading, h3, h2');
        var title = titleEl ? titleEl.textContent.trim().toLowerCase() : '';
        var matches = !q || title.indexOf(q) !== -1;
        card.style.display = matches ? '' : 'none';
        if (matches) anyVisible = true;
      });

      if (!anyVisible && q) {
        noResultsEl = document.createElement('li');
        noResultsEl.className = 'snap-no-results';
        noResultsEl.innerHTML = '<p>No products found.</p>';
        grid.appendChild(noResultsEl);
      }
    }

    function openSearch() {
      if (form.classList.contains('snap-search--open')) return;
      form.classList.add('snap-search--open');
      setTimeout(function() { input.focus(); }, 50);
    }

    function closeSearch() {
      form.classList.remove('snap-search--open');
      input.value = '';
      clearTimeout(searchTimer);
      filterCards('');
    }

    icon && icon.addEventListener('click', openSearch);
    label && label.addEventListener('click', openSearch);

    closeBtn && closeBtn.addEventListener('click', function(e) {
      e.preventDefault();
      closeSearch();
    });

    form.addEventListener('submit', function(e) { e.preventDefault(); });

    input.addEventListener('input', function() {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(function() { filterCards(input.value); }, 200);
    });

    input.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeSearch();
    });

    document.addEventListener('click', function(e) {
      if (form.classList.contains('snap-search--open') && !form.contains(e.target)) {
        closeSearch();
      }
    });
  }

  function initInlineSearch() {
    var forms = document.querySelectorAll('.snap-collection-search');
    Array.prototype.forEach.call(forms, function(form) {
      initOneInlineSearch(form);
    });
  }

  function snapFormatMoney(cents) {
    var fmt =
      window.snapMoneyFormat ||
      (window.Shopify && window.Shopify.money_format) ||
      '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var amountNoDec = String(Math.floor(cents / 100));
    return fmt
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDec)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace(
        '{{amount_no_decimals_with_comma_separator}}',
        amountNoDec.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      );
  }

  /* ==========================================
     PRODUCT PAGE — Quantity × Price Update
     ========================================== */
  function initProductPageQtyPrice() {
    if (!document.querySelector('product-info')) return;

    function getActiveProductInfo() {
      // After variant rendering, old product-info is hidden via display:none; new one is visible
      return (
        document.querySelector('product-info:not([style*="display: none"])') ||
        document.querySelector('product-info')
      );
    }

    function applyQtyPrice() {
      var productInfo = getActiveProductInfo();
      if (!productInfo) return;
      var qtyInput = productInfo.querySelector('.quantity__input');
      var priceEl = productInfo.querySelector(
        '.price--large .price-item--regular',
      );
      var variantScript = productInfo.querySelector('[data-selected-variant]');
      if (!qtyInput || !priceEl || !variantScript) return;
      var variant;
      try {
        variant = JSON.parse(variantScript.textContent);
      } catch (e) {
        return;
      }
      if (!variant || !variant.price) return;
      var qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      priceEl.textContent = snapFormatMoney(variant.price * qty);
    }

    // Listen for quantity changes (quantity-input dispatches 'change' on the input)
    document.addEventListener('change', function (e) {
      if (
        e.target.closest('.quantity__input') &&
        e.target.closest('product-info')
      ) {
        applyQtyPrice();
      }
    });

    // After variant re-render (new product-info inserted), re-apply if qty > 1
    var mainEl = document.querySelector('main') || document.body;
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.tagName && node.tagName.toLowerCase() === 'product-info') {
            setTimeout(function () {
              var productInfo = getActiveProductInfo();
              if (!productInfo) return;
              var qtyInput = productInfo.querySelector('.quantity__input');
              if (qtyInput && parseInt(qtyInput.value, 10) > 1) applyQtyPrice();
            }, 50);
          }
        });
      });
    }).observe(mainEl, { childList: true, subtree: true });
  }

  /* ==========================================
     SLIDESHOW KEYBOARD LOOP
     ========================================== */
  function initSlideshowKeyboardLoop() {
    document.querySelectorAll('slideshow-component').forEach(function (slideshow) {
      slideshow.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var btn = slideshow.querySelector(
          'button[name="' + (e.key === 'ArrowRight' ? 'next' : 'previous') + '"]'
        );
        if (btn) btn.click();
      });
    });
  }

  /* ==========================================
     SLIDESHOW BANNER NAVIGATION
     ========================================== */
  function initSlideBannerNav() {
    document.querySelectorAll('.slideshow__slide[data-url]').forEach(function (slide) {
      var startX = 0;
      var startY = 0;
      slide.addEventListener('pointerdown', function (e) {
        startX = e.clientX;
        startY = e.clientY;
      }, { passive: true });
      slide.addEventListener('click', function (e) {
        if (e.target.closest('button, a')) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;
        if (Math.sqrt(dx * dx + dy * dy) < 10) {
          window.location.href = slide.dataset.url;
        }
      });
    });
  }

  /* ==========================================
     CLEAN URLs (strip ?variant= param)
     ========================================== */
  function initCleanURLs() {
    function stripVariant(url) {
      if (!url) return url;
      try {
        var u = new URL(String(url), window.location.origin);
        u.searchParams.delete('variant');
        return u.pathname + (u.search !== '?' ? u.search : '') + u.hash;
      } catch (e) {
        return url;
      }
    }

    // Patch replaceState so Shopify's variant selection never pollutes the URL
    var _replaceState = history.replaceState.bind(history);
    history.replaceState = function (state, title, url) {
      return _replaceState(state, title, stripVariant(url));
    };

    // Clean the URL on initial load (e.g. shared links with ?variant=)
    if (window.location.search && new URLSearchParams(window.location.search).has('variant')) {
      _replaceState(null, '', stripVariant(window.location.href));
    }
  }

  /* ==========================================
     QUICK-ADD MODAL — Quantity × Price Update
     ========================================== */
  function initQuickAddQtyPrice() {
    function applyPrice(modal) {
      var productInfo = modal.querySelector('product-info');
      if (!productInfo) return;
      var qtyInput = productInfo.querySelector('.quantity__input');
      var priceEl = productInfo.querySelector('.price-item--regular');
      var variantScript = productInfo.querySelector('[data-selected-variant]');
      if (!qtyInput || !priceEl || !variantScript) return;
      var variant;
      try { variant = JSON.parse(variantScript.textContent); } catch (e) { return; }
      if (!variant || !variant.price) return;
      var qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      priceEl.textContent = snapFormatMoney(variant.price * qty);
    }

    // Quantity +/- buttons dispatch 'change' on the input after updating the value
    document.addEventListener('change', function (e) {
      if (!e.target.closest('.quantity__input')) return;
      var modal = e.target.closest('quick-add-modal');
      if (!modal) return;
      applyPrice(modal);
    });

    // After variant switch Dawn re-renders product-info — re-apply if qty > 1
    document.addEventListener('variant:change', function (e) {
      var modal = e.target.closest('quick-add-modal');
      if (!modal) return;
      setTimeout(function () { applyPrice(modal); }, 50);
    });

    // Fallback: watch for product-info DOM changes inside any open modal
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (!node.querySelector) return;
          var modal = node.closest ? node.closest('quick-add-modal') : null;
          if (!modal) modal = node.querySelector('quick-add-modal');
          if (modal) setTimeout(function () { applyPrice(modal); }, 50);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ==========================================
     INSTANT NAVIGATION — Speculation Rules + prefetch fallback

     Modern Chrome/Edge (108+): Speculation Rules API with prerender.
     The browser fully renders destination pages in the background so
     clicking a link is truly instant — zero wait.

     Older browsers: <link rel="prefetch"> fetches the HTML into cache
     ahead of time, making navigation significantly faster.
     ========================================== */
  function initPrefetch() {
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;

    // Speculation Rules: Chrome 108+ / Edge — prerender pages before click
    if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
      const script = document.createElement('script');
      script.type = 'speculationrules';
      script.textContent = JSON.stringify({
        prefetch: [
          {
            where: {
              and: [
                { selector_matches: '.snap-sidenav a, header a' },
                { not: { href_matches: '/cart*'     } },
                { not: { href_matches: '/checkout*' } },
                { not: { href_matches: '/account*'  } }
              ]
            },
            eagerness: 'moderate'
          },
          {
            where: {
              and: [
                { href_matches: '/*' },
                { not: { href_matches: '/cart*'     } },
                { not: { href_matches: '/checkout*' } },
                { not: { href_matches: '/account*'  } }
              ]
            },
            eagerness: 'moderate'
          }
        ]
      });
      document.head.appendChild(script);
      return; // Speculation Rules covers everything; no manual prefetch needed
    }

    // Fallback: manual <link rel="prefetch"> for other browsers
    const prefetched = new Set();

    function prefetch(url) {
      if (prefetched.has(url)) return;
      prefetched.add(url);
      const el = document.createElement('link');
      el.rel  = 'prefetch';
      el.href = url;
      el.as   = 'document';
      document.head.appendChild(el);
    }

    function resolveInternal(el) {
      const a = el.closest ? el.closest('a[href]') : null;
      if (!a || a.target === '_blank') return null;
      const raw = a.getAttribute('href') || '';
      if (!raw || /^(#|javascript|mailto:|tel:)/.test(raw)) return null;
      try {
        const url = new URL(raw, location.href);
        if (url.origin !== location.origin) return null;
        if (url.pathname === location.pathname && url.search === location.search) return null;
        return url.href;
      } catch (_) { return null; }
    }

    // 1. Prefetch links as they scroll into view
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const href = resolveInternal(entry.target);
          if (href) prefetch(href);
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px 200px 0px' });
      document.querySelectorAll('a[href]').forEach((a) => io.observe(a));
    }

    // 2. Desktop: prefetch on hover
    document.addEventListener('mouseover', (e) => {
      const href = resolveInternal(e.target);
      if (href) prefetch(href);
    });

    // 3. Mobile: prefetch on finger-down
    document.addEventListener('touchstart', (e) => {
      const href = resolveInternal(e.target);
      if (href) prefetch(href);
    }, { passive: true });
  }

  /* ==========================================
     EXPLORE MORE BUTTON (last card in collection sliders)
     ========================================== */
  function initExploreMoreButtons() {
    document.querySelectorAll('.snap-col-group').forEach(function (group) {
      var viewAllLink = group.querySelector('.snap-col-group__view-all');
      var collectionUrl = viewAllLink ? viewAllLink.href : null;

      // Fallback: derive URL from slider ID (e.g. SnapSlider-home-accents or Slider-home-accents)
      if (!collectionUrl) {
        var sliderUlId = group.querySelector('ul.slider[id]');
        if (sliderUlId) {
          var idMatch = sliderUlId.id.match(/Slider-(.+)$/i);
          if (idMatch) collectionUrl = '/collections/' + idMatch[1];
        }
      }

      if (!collectionUrl) return;

      var sliderComp = group.querySelector('slider-component');
      if (!sliderComp) return;
      if (sliderComp.querySelector('.snap-explore-more')) return;

      var sliderUl = group.querySelector('ul.slider');
      var slides = group.querySelectorAll('li.slider__slide');
      if (!slides.length) return;

      // Only show when the slider is full (slides rendered == limit).
      // If fewer slides than limit, the collection has fewer total products.
      var productsLimit = parseInt(group.dataset.productsLimit || '8', 10);
      if (slides.length < productsLimit) return;

      var btn = document.createElement('a');
      btn.href = collectionUrl;
      btn.className = 'snap-explore-more';
      btn.setAttribute('aria-label', 'Explore all products in this collection');
      btn.innerHTML = 'Explore more <svg class="snap-explore-more__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      sliderComp.appendChild(btn);

      function updateBtn() {
        if (!sliderUl) return;
        var atEnd = sliderUl.scrollLeft + sliderUl.clientWidth >= sliderUl.scrollWidth - 10;
        btn.classList.toggle('snap-explore-more--visible', atEnd);
      }

      if (sliderUl) sliderUl.addEventListener('scroll', updateBtn, { passive: true });
      requestAnimationFrame(updateBtn);
    });
  }

  /* ==========================================
     CART DRAWER RECOMMENDATIONS
     Uses Shopify's /recommendations/products.json (intent=related)
     to surface products frequently bought together by other customers.
     ========================================== */
  function initCartRecommendations() {
    /* Re-query at call time — container always exists in DOM now */
    function getContainer() { return document.getElementById('CartDrawerRecs'); }
    if (!getContainer()) return;

    var lastKey = '';
    var busy = false;

    function esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    function money(cents) {
      return 'EGP ' + Math.round(cents / 100).toLocaleString('en-US');
    }

    /* Map color names to hex values (mirrors swatch.liquid) */
    var COLOR_MAP = {
      black:'#000',white:'#fff',red:'#cc0000',green:'#228b22',blue:'#2255aa',
      yellow:'#ffd700',orange:'#ff8c00',purple:'#800080',pink:'#ff69b4',
      brown:'#8b4513',grey:'#808080',gray:'#808080',silver:'#c0c0c0',
      gold:'#d4af37',beige:'#f5f5dc',ivory:'#fffff0',warmwhite:'#fff5dc',
      navy:'#001f5b',navyblue:'#001f5b',teal:'#008080',maroon:'#800000',
      coral:'#ff7f50',salmon:'#fa8072',tan:'#d2b48c',olive:'#808000',
      lavender:'#e6e6fa',violet:'#ee82ee',indigo:'#4b0082',turquoise:'#40e0d0',
      amber:'#ffbf00',cream:'#fffdd0',mint:'#98ff98',charcoal:'#36454f',
      burgundy:'#800020',darkgrey:'#404040',darkgray:'#404040',darkblue:'#00008b',
      darkgreen:'#006400',darkred:'#8b0000',lightgrey:'#d3d3d3',lightgray:'#d3d3d3',
      lightblue:'#add8e6',lightgreen:'#90ee90',rose:'#ff007f',khaki:'#c3b091',lightkhaki:'#D6C9AF',
      lime:'#00ff00',cyan:'#00ffff',magenta:'#ff00ff',sky:'#87ceeb',skyblue:'#87ceeb',
      peach:'#ffcba4',sand:'#c2b280',stone:'#918e85',taupe:'#483c32',
      copper:'#b87333',bronze:'#cd7f32',rust:'#b7410e',chocolate:'#7b3f00',
      matteblack:'#1c1c1c',mattewhite:'#f5f5f5',mattegold:'#c9a84c',
      mattesilver:'#9e9e9e',mattegrey:'#696969',mattegray:'#696969',mattedarkgrey:'#4B5563',mattedarkgray:'#4B5563',
      mattebrown:'#6b4226',matteblue:'#2255aa',mattegreen:'#228b22',
      matteolive:'#556b2f',mattenavy:'#001f5b',mattenavyblue:'#001f5b'
    };

    function colorHex(name) {
      return COLOR_MAP[name.toLowerCase().replace(/[\s-]/g, '')] || null;
    }

    function isColorOption(name) {
      var n = name.toLowerCase();
      return n.includes('color') || n.includes('colour');
    }

    function buildVariantPicker(product) {
      /* Group options */
      var options = product.options || [];
      var variants = product.variants || [];
      if (variants.length <= 1) return '';

      var html = '';
      options.forEach(function(opt, idx) {
        var values = [];
        variants.forEach(function(v) {
          var val = v['option' + (idx + 1)];
          if (val && values.indexOf(val) === -1) values.push(val);
        });

        if (isColorOption(opt) && values.length > 1) {
          /* Render color swatches */
          html += '<div class="cart-rec-option" data-option-idx="' + idx + '">';
          values.forEach(function(v, vi) {
            var hex = colorHex(v);
            var bg = hex ? 'background:' + hex + ';' : 'background:#ddd;';
            var border = (!hex || hex === '#fff' || hex === '#ffffff' || hex === '#fffff0') ? 'border:1px solid #ccc;' : '';
            html += '<button type="button" class="cart-rec-swatch' + (vi === 0 ? ' cart-rec-swatch--active' : '') + '" ' +
              'data-value="' + esc(v) + '" data-option-idx="' + idx + '" ' +
              'title="' + esc(v) + '" style="' + bg + border + '">' +
              '<span class="visually-hidden">' + esc(v) + '</span>' +
              '</button>';
          });
          html += '</div>';
        } else {
          /* Render pill buttons for size / other options */
          html += '<div class="cart-rec-option" data-option-idx="' + idx + '">';
          values.forEach(function(v, vi) {
            html += '<button type="button" class="cart-rec-pill' + (vi === 0 ? ' cart-rec-pill--active' : '') + '" ' +
              'data-value="' + esc(v) + '" data-option-idx="' + idx + '">' +
              esc(v) + '</button>';
          });
          html += '</div>';
        }
      });
      return html;
    }

    function getSelectedVariant(card, product) {
      var variants = product.variants || [];
      if (variants.length <= 1) return variants[0];

      var options = product.options || [];
      var selected = {};
      options.forEach(function(opt, idx) {
        var active = card.querySelector('[data-option-idx="' + idx + '"] .cart-rec-swatch--active, [data-option-idx="' + idx + '"] .cart-rec-pill--active');
        if (active) selected[idx] = active.dataset.value;
      });

      return variants.find(function(v) {
        return options.every(function(_, idx) {
          return !selected[idx] || v['option' + (idx + 1)] === selected[idx];
        });
      }) || variants.find(function(v) { return v.available; }) || variants[0];
    }

    function buildCard(product) {
      var productUrl = product.url || ('/products/' + product.handle);
      /* imgToSrc: handles plain string URLs AND image objects {src, url, id, ...} */
      function imgToSrc(v) {
        if (!v) return '';
        if (typeof v === 'string') return v;
        return v.src || v.url || '';
      }
      var img0 = product.images && product.images[0];
      var rawSrc = imgToSrc(img0) || imgToSrc(product.featured_image);
      var imgSrc = rawSrc ? rawSrc.replace(/(\.[^.?]+)(\?.*)?$/, '_240x$1$2') : '';
      var variants = product.variants || [];
      var firstAvail = variants.find(function(v) { return v.available; }) || variants[0];
      if (!firstAvail) return null;
      var price = money(firstAvail.price);
      var hasVariants = variants.length > 1;
      var isAvail = firstAvail.available;

      var card = document.createElement('div');
      card.className = 'cart-rec-card';
      try {
        card.dataset.productJson = JSON.stringify({
          id: product.id,
          options: product.options || [],
          variants: variants.map(function(v) {
            return { id: v.id, available: v.available, price: v.price, option1: v.option1, option2: v.option2, option3: v.option3 };
          })
        });
      } catch (_) {}

      card.innerHTML =
        (imgSrc
          ? '<a href="' + esc(productUrl) + '" class="cart-rec-card__img-wrap" tabindex="-1" aria-hidden="true">' +
              '<img class="cart-rec-card__img" src="' + esc(imgSrc) + '" alt="' + esc(product.title) + '" loading="lazy" width="240" height="180">' +
            '</a>'
          : '') +
        '<div class="cart-rec-card__body">' +
          '<a href="' + esc(productUrl) + '" class="cart-rec-card__title">' + esc(product.title) + '</a>' +
          '<p class="cart-rec-card__price" data-base-price="' + firstAvail.price + '">' + price + '</p>' +
          (hasVariants ? '<div class="cart-rec-card__options">' + buildVariantPicker(product) + '</div>' : '') +
          '<button type="button" class="cart-rec-card__add"' + (!isAvail ? ' disabled' : '') + '>' +
            (isAvail
              ? '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add'
              : 'Sold out') +
          '</button>' +
        '</div>';

      return card;
    }

    function renderRecs(products) {
      var container = getContainer();
      if (!container) return;
      container.innerHTML = '';

      if (!products || !products.length) {
        container.hidden = true;
        return;
      }

      /* Build cards first — only show section if at least one card renders */
      var track = document.createElement('div');
      track.className = 'cart-recs__track';
      products.forEach(function(p) {
        try {
          var card = buildCard(p);
          if (card) track.appendChild(card);
        } catch (_) {}
      });

      if (!track.children.length) {
        container.hidden = true;
        return;
      }

      var label = document.createElement('p');
      label.className = 'cart-recs__label';
      label.textContent = 'Others also bought';
      container.appendChild(label);
      container.appendChild(track);
      container.hidden = false;
    }

    function fetchRecs(productId, cartProductIds, intent) {
      return fetch('/recommendations/products.json?product_id=' + productId + '&limit=8&intent=' + intent)
        .then(function(r) { return r.json(); })
        .then(function(data) {
          return (data.products || []).filter(function(p) {
            return cartProductIds.indexOf(p.id) === -1;
          }).slice(0, 6);
        });
    }

    function load(force) {
      if (busy) return;
      var container = getContainer();
      if (!container) return;
      fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          if (!cart.items || !cart.items.length) { var c = getContainer(); if (c) c.hidden = true; return; }
          var cartProductIds = cart.items.map(function(i) { return i.product_id; });
          var key = cartProductIds.join(',');
          if (!force && key === lastKey) return;
          lastKey = key;
          busy = true;
          var pid = cartProductIds[0];
          return fetchRecs(pid, cartProductIds, 'complementary')
            .then(function(recs) {
              if (recs.length) return recs;
              return fetchRecs(pid, cartProductIds, 'related');
            })
            .then(function(recs) { renderRecs(recs); })
            .finally(function() { busy = false; });
        })
        .catch(function() { busy = false; });
    }

    /* Option selection (swatches + pills) */
    document.addEventListener('click', function(e) {
      if (!e.target.closest('#CartDrawerRecs')) return;
      var swatch = e.target.closest('.cart-rec-swatch, .cart-rec-pill');
      if (swatch) {
        var card = swatch.closest('.cart-rec-card');
        var optionGroup = swatch.closest('.cart-rec-option');
        optionGroup.querySelectorAll('.cart-rec-swatch, .cart-rec-pill').forEach(function(s) {
          s.classList.remove('cart-rec-swatch--active', 'cart-rec-pill--active');
        });
        swatch.classList.add(swatch.classList.contains('cart-rec-swatch') ? 'cart-rec-swatch--active' : 'cart-rec-pill--active');

        /* Update displayed price to match selected variant */
        try {
          var data = JSON.parse(card.dataset.productJson);
          var variant = getSelectedVariant(card, data);
          if (variant) {
            var priceEl = card.querySelector('.cart-rec-card__price');
            if (priceEl) priceEl.textContent = money(variant.price);
            var addBtn = card.querySelector('.cart-rec-card__add');
            if (addBtn) {
              addBtn.disabled = !variant.available;
              if (variant.available) {
                addBtn.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add';
              } else {
                addBtn.textContent = 'Sold out';
              }
            }
          }
        } catch (_) {}
        return;
      }

      /* Quick add */
      var addBtn = e.target.closest('.cart-rec-card__add');
      if (!addBtn || addBtn.disabled) return;
      var card = addBtn.closest('.cart-rec-card');
      var variantId;
      try {
        var data = JSON.parse(card.dataset.productJson);
        var variant = getSelectedVariant(card, data);
        variantId = variant && variant.id;
      } catch (_) {}
      if (!variantId) return;

      addBtn.disabled = true;
      addBtn.innerHTML = '<span class="cart-rec-card__spinner"></span>';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
      .then(function(r) { return r.json(); })
      .then(function() {
        addBtn.textContent = '✓ Added';
        addBtn.classList.add('cart-rec-card__add--done');
        card.classList.add('cart-rec-card--done');
        /* Notify cart system so count bubble + free shipping bar update */
        fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(function(cartData) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recs', cartData: cartData });
            }
            document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { cartData: cartData } }));
          });
        /* Refresh recs list after a moment (remove the just-added product) */
        setTimeout(function() {
          lastKey = '';
          load(true);
        }, 1200);
      })
      .catch(function() {
        addBtn.disabled = false;
        addBtn.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add';
      });
    });

    /* Watch for Dawn's onCartUpdate() replacing cart-drawer-items in the DOM.
       That's the real trigger — we re-render immediately after it settles. */
    var recsTimer = null;
    function scheduleLoad() {
      clearTimeout(recsTimer);
      recsTimer = setTimeout(function() { lastKey = ''; load(true); }, 150);
    }

    /* Observe .drawer__inner children — cart-drawer-items is replaced there */
    var drawerInner = document.querySelector('cart-drawer .drawer__inner');
    if (drawerInner) {
      new MutationObserver(function(mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].type === 'childList') { scheduleLoad(); break; }
        }
      }).observe(drawerInner, { childList: true });
    }

    /* Also watch .drawer__footer — .cart-drawer__footer is replaced there */
    var drawerFooter = document.querySelector('cart-drawer .drawer__footer');
    if (drawerFooter) {
      new MutationObserver(function(mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].type === 'childList') { scheduleLoad(); break; }
        }
      }).observe(drawerFooter, { childList: true });
    }

    /* Fallback: body overflow-hidden — starts/stops the poll as the drawer opens/closes */
    var pollInterval = null;
    new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'attributes') {
          if (document.body.classList.contains('overflow-hidden')) {
            scheduleLoad();
            if (!pollInterval) {
              pollInterval = setInterval(function() {
                var container = getContainer();
                if (!container || !container.hidden) return;
                if (busy) return;
                lastKey = '';
                load(true);
              }, 500);
            }
          } else {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          break;
        }
      }
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

    /* Initial load */
    load(true);
  }

  /* ==========================================
     INIT ALL
     ========================================== */
  function init() {
    initCleanURLs();
    initPrefetch();
    initLoader();
    initImageSkeletons();
    initCursor();
    initInlineSearch();
    initSideNav();
    initSideNavActive();
    initTypewriter();
    initHeroParallax();
    initMarquee();
    initHeaderScroll();
    initAnnouncementBar();
    initProductPageQtyPrice();
    initQuickAddQtyPrice();
    initSlideBannerNav();
    initSlideshowKeyboardLoop();
    initExploreMoreButtons();

    // Cart recommendations are deferred — no network calls until the drawer first opens
    (function () {
      var recInited = false;
      var cartTrigger = new MutationObserver(function () {
        if (document.body.classList.contains('overflow-hidden')) {
          cartTrigger.disconnect();
          if (!recInited) { recInited = true; initCartRecommendations(); }
        }
      });
      cartTrigger.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    })();

    // Defer heavier effects until DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initScrollAnimations();
        initCardTilt();
        initMagneticButtons();
        initImageHover();
        animateCounters();
        initGridGlow();
      });
    } else {
      initScrollAnimations();
      initCardTilt();
      initMagneticButtons();
      initImageHover();
      animateCounters();
      initGridGlow();
    }
  }

  function initCardSlideshows() {
    document.querySelectorAll('[data-slideshow]').forEach(function (el) {
      var img = el.querySelector('.snap-slide-img');
      var dots = el.querySelectorAll('.snap-card-slideshow__dot');
      var prevBtn = el.querySelector('.snap-slide-btn--prev');
      var nextBtn = el.querySelector('.snap-slide-btn--next');
      if (!img) return;

      var firstSrc = img.src;
      var extras = (el.dataset.slideshow || '').split('|').filter(Boolean);
      var all = [firstSrc].concat(extras);
      if (all.length <= 1) return;

      var current = 0;
      var touchStartX = 0;

      function goTo(index) {
        dots[current] &&
          dots[current].classList.remove('snap-card-slideshow__dot--active');
        current = (index + all.length) % all.length;
        img.src = all[current];
        dots[current] &&
          dots[current].classList.add('snap-card-slideshow__dot--active');
      }

      // Arrow buttons
      prevBtn &&
        prevBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          goTo(current - 1);
        });
      nextBtn &&
        nextBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          goTo(current + 1);
        });

      // Dot clicks
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          goTo(i);
        });
      });

      // Touch swipe
      el.addEventListener(
        'touchstart',
        function (e) {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true },
      );

      el.addEventListener(
        'touchend',
        function (e) {
          var dx = e.changedTouches[0].screenX - touchStartX;
          if (Math.abs(dx) > 40) {
            goTo(dx < 0 ? current + 1 : current - 1);
          }
        },
        { passive: true },
      );
    });
  }

  init();
  initCardSlideshows();
})();

// Customer Care accordion (mobile only)
(function () {
  var btn = document.querySelector('.snap-footer-center__heading');
  var links = document.getElementById('snap-care-links');
  if (!btn || !links) return;

  btn.addEventListener('click', function () {
    if (window.innerWidth > 749) return;
    var expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!expanded));
    links.classList.toggle('is-open', !expanded);
  });
})();

// Global slider bar indicators — appended directly to slider-component so they are
// never affected by .slider-buttons / .slider-counter visibility rules.
// Works via two independent paths:
//   1. slideChanged from SliderComponent (primary)
//   2. Direct DOM measurement after paint + direct scroll listener (fallback)
(function () {
  function barWidths(total) {
    if (total <= 2) return { inactive: 100, active: 280 };
    if (total <= 4) return { inactive: 70,  active: 220 };
    if (total <= 7) return { inactive: 45,  active: 140 };
    return { inactive: 28, active: 90 };
  }

  function initSliderBars(sliderComp) {
    if (sliderComp.dataset.snapBars) return;
    sliderComp.dataset.snapBars = '1';

    var sliderButtons = sliderComp.querySelector('.slider-buttons');

    // Append bars container directly to slider-component — no CSS inheritance issues
    var barsDiv = document.createElement('div');
    barsDiv.className = 'snap-slider-bars';
    barsDiv.setAttribute('aria-hidden', 'true');
    sliderComp.appendChild(barsDiv);

    var lastTotal = 0;
    var directOffset = 0; // item offset used by the direct-scroll fallback

    // --- Prev/next arrows (desktop) — share the same scroll element as the bars ---
    // Built lazily, only once a slider is confirmed multi-page (see buildBars).
    var arrowsBuilt = false;
    var arrowPrev, arrowNext;

    function getSliderUl() {
      return sliderComp.querySelector('ul.slider') || sliderComp.querySelector('[id^="Slider-"]');
    }

    function cardOffset(ul) {
      var slides = sliderComp.querySelectorAll('.slider__slide');
      if (slides.length > 1) {
        var o = slides[1].offsetLeft - slides[0].offsetLeft;
        if (o > 0) return o;
      }
      return Math.round(ul.clientWidth * 0.8); // fallback: ~one viewport
    }

    function updateArrows() {
      if (!arrowsBuilt) return;
      var ul = getSliderUl();
      if (!ul) return;
      var maxScroll = ul.scrollWidth - ul.clientWidth;
      arrowPrev.disabled = ul.scrollLeft <= 2;
      arrowNext.disabled = ul.scrollLeft >= maxScroll - 2;
    }

    function buildArrows() {
      if (arrowsBuilt) return;
      var ul = getSliderUl();
      if (!ul) return;
      arrowsBuilt = true;

      arrowPrev = document.createElement('button');
      arrowPrev.type = 'button';
      arrowPrev.className = 'snap-slider-arrow snap-slider-arrow--prev';
      arrowPrev.setAttribute('aria-label', 'Previous products');
      arrowPrev.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      arrowNext = document.createElement('button');
      arrowNext.type = 'button';
      arrowNext.className = 'snap-slider-arrow snap-slider-arrow--next';
      arrowNext.setAttribute('aria-label', 'Next products');
      arrowNext.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      arrowPrev.addEventListener('click', function () {
        var u = getSliderUl();
        if (u) u.scrollBy({ left: -cardOffset(u), behavior: 'smooth' });
      });
      arrowNext.addEventListener('click', function () {
        var u = getSliderUl();
        if (u) u.scrollBy({ left: cardOffset(u), behavior: 'smooth' });
      });

      sliderComp.appendChild(arrowPrev);
      sliderComp.appendChild(arrowNext);
      sliderComp.classList.add('snap-has-arrows'); // CSS hook for position: relative

      ul.addEventListener('scroll', updateArrows, { passive: true });
      updateArrows();
    }

    function setWidths(total) {
      var w = barWidths(total);
      barsDiv.style.setProperty('--bar-inactive', w.inactive + 'px');
      barsDiv.style.setProperty('--bar-active',   w.active   + 'px');
    }

    function buildBars(total, current) {
      if (total < 2) return; // single-page sliders get no bars
      lastTotal = total;
      // Hide the built-in counter only when we are actually showing bars
      if (sliderButtons) sliderButtons.style.display = 'none';
      barsDiv.innerHTML = '';
      setWidths(total);
      for (var i = 0; i < total; i++) {
        var bar = document.createElement('span');
        bar.className = 'snap-slider-bar' + (i === current - 1 ? ' snap-slider-bar--active' : '');
        barsDiv.appendChild(bar);
      }
      buildArrows(); // multi-page confirmed — add desktop prev/next arrows
    }

    function setActive(current) {
      barsDiv.querySelectorAll('.snap-slider-bar').forEach(function (b, idx) {
        b.classList.toggle('snap-slider-bar--active', idx === current - 1);
      });
    }

    // --- Path 1: SliderComponent's slideChanged event ---
    sliderComp.addEventListener('slideChanged', function (e) {
      var total   = sliderComp.totalPages || 1;
      var current = (e.detail && e.detail.currentPage) || 1;
      if (total < 2) return; // not a multi-page carousel
      if (total !== lastTotal) {
        buildBars(total, current);
      } else {
        setActive(current);
      }
    });

    // Immediate build if SliderComponent already computed pages (paint was early)
    if ((sliderComp.totalPages || 0) >= 2) {
      buildBars(sliderComp.totalPages, sliderComp.currentPage || 1);
    }

    // --- Path 2: Direct DOM fallback (for sliders not yet painted at script time) ---
    // Uses class selectors so it works regardless of element IDs.
    function measureAndBuild() {
      if (lastTotal > 0) return; // Path 1 already handled it
      var ul = sliderComp.querySelector('ul.slider');
      if (!ul) return;
      var vis = Array.from(sliderComp.querySelectorAll('.slider__slide'))
                     .filter(function (el) { return el.clientWidth > 0; });
      if (vis.length < 2) return;
      directOffset = vis[1].offsetLeft - vis[0].offsetLeft;
      if (directOffset === 0) return;
      var perPage = Math.floor((ul.clientWidth - vis[0].offsetLeft) / directOffset);
      var total   = Math.max(1, vis.length - perPage + 1);
      if (total < 2) return; // only one page — nothing to indicate
      var current = Math.round(ul.scrollLeft / directOffset) + 1;
      buildBars(total, current);

      // Direct scroll listener so the active bar updates on manual scroll
      ul.addEventListener('scroll', function () {
        if (!directOffset) return;
        var cur = Math.max(1, Math.min(lastTotal, Math.round(ul.scrollLeft / directOffset) + 1));
        setActive(cur);
      }, { passive: true });
    }

    // Run after first paint (requestAnimationFrame fires after layout/paint)
    requestAnimationFrame(function () {
      measureAndBuild();
      // Belt-and-suspenders: also retry once at 300 ms for lazy-loaded or CSS-deferred sliders
      if (lastTotal === 0) setTimeout(measureAndBuild, 300);
    });
  }

  document.querySelectorAll('slider-component').forEach(function (el) {
    // Skip product-page media gallery sliders — they have their own thumbnail UI
    var id = el.id || '';
    if (id.indexOf('GalleryViewer') === 0 || id.indexOf('GalleryThumbnails') === 0) return;
    initSliderBars(el);
  });
})();

// Judge.me Reviews — modern image-first carousel
// Injected via JS so our styles load AFTER Judge.me's async CSS and win the cascade.
(function () {
  var CARD_W = 256;
  var CSS = [
    /* carousel track */
    '.jdgm-rev-widg__reviews{display:flex!important;flex-wrap:nowrap!important;gap:16px!important;',
    'overflow-x:auto!important;scroll-snap-type:x mandatory!important;scroll-behavior:smooth!important;',
    'padding:4px 2px 16px!important;-ms-overflow-style:none!important;scrollbar-width:none!important;align-items:stretch!important;}',
    '.jdgm-rev-widg__reviews::-webkit-scrollbar{display:none!important;}',
    /* card */
    '.jdgm-rev{flex:0 0 ' + CARD_W + 'px!important;width:' + CARD_W + 'px!important;min-width:0!important;',
    'scroll-snap-align:start!important;border-radius:14px!important;overflow:hidden!important;',
    'border:1px solid #e8d9b0!important;border-top:1px solid #e8d9b0!important;background:#F5ECD5!important;',
    'box-shadow:0 2px 12px rgba(0,0,0,.07)!important;display:flex!important;flex-direction:column!important;',
    'margin:0!important;padding:0!important;transition:box-shadow .2s,transform .2s!important;}',
    '.jdgm-rev:hover{box-shadow:0 6px 28px rgba(0,0,0,.12)!important;transform:translateY(-2px)!important;}',
    /* hide original header + pics (we rebuild them above the body) */
    '.jdgm-rev .jdgm-rev__header{display:none!important;}',
    '.jdgm-rev .jdgm-rev__pics{display:none!important;}',
    /* rebuilt elements */
    '.snap-rev-img{width:100%;flex-shrink:0;overflow:hidden;}',
    '.snap-rev-img img{width:100%!important;height:180px!important;object-fit:cover!important;display:block!important;border-radius:0!important;}',
    '.snap-rev-info{padding:12px 14px 6px;flex-shrink:0;}',
    '.snap-rev-stars{display:block;margin-bottom:5px;font-size:14px;}',
    '.snap-rev-meta{display:flex;align-items:center;gap:5px;flex-wrap:wrap;}',
    '.snap-rev-name{font-weight:700;font-size:12px;color:#111;}',
    '.snap-rev-dot{color:#ccc;font-size:10px;line-height:1;}',
    '.snap-rev-date{font-size:11px;color:#999;}',
    /* body text */
    '.jdgm-rev .jdgm-rev__body{padding:6px 14px 14px!important;flex:1!important;display:flex!important;flex-direction:column!important;}',
    '.jdgm-rev .jdgm-rev__title{display:none!important;}',
    '.jdgm-rev .jdgm-rev__content{font-size:13px!important;line-height:1.6!important;color:#555!important;',
    'margin:0!important;display:-webkit-box!important;-webkit-line-clamp:4!important;-webkit-box-orient:vertical!important;overflow:hidden!important;}',
    /* load-more button */
    '.jdgm-paginate__load-more{display:inline-flex!important;align-items:center!important;justify-content:center!important;',
    'margin-top:20px!important;padding:10px 24px!important;background:#0d2545!important;color:#fff!important;',
    'border:none!important;border-radius:8px!important;font-size:13px!important;font-weight:700!important;cursor:pointer!important;}',
    '.jdgm-paginate__load-more:hover{background:#1a3a6c!important;}',
    /* mobile */
    '@media(max-width:749px){.jdgm-rev{flex:0 0 calc(88vw - 24px)!important;width:calc(88vw - 24px)!important;}}',
    /* nav arrows (our own injected elements — no Judge.me conflict) */
    '.jdgm-carousel-nav{display:flex;justify-content:flex-end;gap:8px;margin-top:14px;}',
    '.jdgm-carousel-btn{width:36px;height:36px;border:1.5px solid #0d2545;background:#fff;border-radius:50%;',
    'cursor:pointer;display:flex;align-items:center;justify-content:center;color:#0d2545;',
    'transition:background .18s,color .18s;padding:0;line-height:1;}',
    '.jdgm-carousel-btn svg{pointer-events:none;}',
    '.jdgm-carousel-btn:hover{background:#0d2545;color:#fff;}',
    '.jdgm-carousel-btn:disabled{opacity:.28;cursor:default;pointer-events:none;}',
  ].join('');

  function injectCSS() {
    if (document.getElementById('snap-jdgm-css')) return;
    var s = document.createElement('style');
    s.id = 'snap-jdgm-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function imgSrc(el) {
    if (!el) return '';
    return el.dataset.src || el.getAttribute('src') || '';
  }

  function styleCard(rev) {
    if (rev.dataset.snapStyled) return;
    rev.dataset.snapStyled = '1';

    var header  = rev.querySelector('.jdgm-rev__header');
    var picsDiv = rev.querySelector('.jdgm-rev__pics');
    var body    = rev.querySelector('.jdgm-rev__body');

    /* extract data from Judge.me's hidden elements */
    var starsEl  = header && header.querySelector('.jdgm-rev__rating');
    var starsHTML = starsEl ? starsEl.outerHTML : '';
    var authorEl = header && (header.querySelector('.jdgm-rev__author-name') || header.querySelector('.jdgm-rev__author'));
    var authorTxt = authorEl ? authorEl.textContent.trim() : '';
    var dateEl   = header && header.querySelector('.jdgm-rev__timestamp');
    var dateTxt  = dateEl ? dateEl.textContent.trim() : '';
    var picImg   = picsDiv && picsDiv.querySelector('img');
    var picSrc   = imgSrc(picImg);

    /* image section (only if review has a photo) */
    var imageHTML = '';
    if (picSrc) {
      imageHTML = '<div class="snap-rev-img"><img src="' + picSrc + '" alt="" loading="lazy"></div>';
    }

    /* info row: stars + name · date */
    var metaHTML =
      (authorTxt ? '<span class="snap-rev-name">' + authorTxt + '</span>' : '') +
      (dateTxt   ? '<span class="snap-rev-dot">·</span><span class="snap-rev-date">' + dateTxt + '</span>' : '');

    var infoHTML =
      '<div class="snap-rev-info">' +
        (starsHTML ? '<span class="snap-rev-stars">' + starsHTML + '</span>' : '') +
        (metaHTML  ? '<div class="snap-rev-meta">' + metaHTML + '</div>' : '') +
      '</div>';

    /* prepend rebuilt sections before .jdgm-rev__body */
    var frag = document.createElement('div');
    frag.innerHTML = imageHTML + infoHTML;
    while (frag.firstChild) rev.insertBefore(frag.firstChild, body);
  }

  function initJudgeMeCarousel() {
    var reviews = document.querySelector('.jdgm-rev-widg__reviews');
    if (!reviews || reviews.dataset.carouselInit) return;
    reviews.dataset.carouselInit = '1';

    injectCSS();

    /* style existing cards */
    reviews.querySelectorAll('.jdgm-rev').forEach(styleCard);

    /* watch for "Load more" adding new cards */
    new MutationObserver(function () {
      reviews.querySelectorAll('.jdgm-rev:not([data-snap-styled])').forEach(styleCard);
      sync();
    }).observe(reviews, { childList: true });

    /* nav arrows */
    var widg = reviews.closest('.jdgm-rev-widg') || reviews.parentElement;
    var nav  = document.createElement('div');
    nav.className = 'jdgm-carousel-nav';
    nav.innerHTML =
      '<button class="jdgm-carousel-btn" data-dir="prev" aria-label="Previous reviews">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>' +
      '</button>' +
      '<button class="jdgm-carousel-btn" data-dir="next" aria-label="Next reviews">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>' +
      '</button>';
    widg.appendChild(nav);

    var prevBtn = nav.querySelector('[data-dir="prev"]');
    var nextBtn = nav.querySelector('[data-dir="next"]');

    function cardWidth() {
      var c = reviews.querySelector('.jdgm-rev');
      return c ? c.offsetWidth + (parseFloat(getComputedStyle(reviews).gap) || 16) : CARD_W + 16;
    }

    prevBtn.addEventListener('click', function () { reviews.scrollBy({ left: -cardWidth(), behavior: 'smooth' }); });
    nextBtn.addEventListener('click', function () { reviews.scrollBy({ left:  cardWidth(), behavior: 'smooth' }); });

    function sync() {
      prevBtn.disabled = reviews.scrollLeft <= 1;
      nextBtn.disabled = reviews.scrollLeft + reviews.clientWidth >= reviews.scrollWidth - 2;
    }
    reviews.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* Judge.me loads async — watch for the widget */
  function watch() {
    if (document.querySelector('.jdgm-rev-widg__reviews')) {
      initJudgeMeCarousel();
      return;
    }
    var obs = new MutationObserver(function (mutations, self) {
      if (document.querySelector('.jdgm-rev-widg__reviews')) {
        self.disconnect();
        /* small delay so Judge.me finishes populating the widget */
        setTimeout(initJudgeMeCarousel, 100);
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();

/* ── Quick-add prefetch on card hover ── */
(function () {
  var cache = new Map();

  /* Start fetching the product page as soon as the pointer enters the card */
  document.addEventListener('mouseover', function (e) {
    var card = e.target.closest('.card-wrapper');
    if (!card) return;
    var btn = card.querySelector('[data-product-url]');
    if (!btn) return;
    var url = btn.getAttribute('data-product-url');
    if (url && !cache.has(url)) {
      cache.set(url, fetch(url).then(function (r) { return r.text(); }));
    }
  });

  /* Patch QuickAddModal.show() to use the prefetched result */
  customElements.whenDefined('quick-add-modal').then(function () {
    var QAM = customElements.get('quick-add-modal');
    var proto = QAM.prototype;
    var parentProto = Object.getPrototypeOf(proto); // ModalDialog.prototype
    var _orig = proto.show;

    proto.show = function (opener) {
      var url = opener.getAttribute('data-product-url');
      if (!cache.has(url)) return _orig.call(this, opener);

      var modal = this;
      var spinner = opener.querySelector('.loading__spinner');
      opener.setAttribute('aria-disabled', 'true');
      opener.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');

      cache.get(url)
        .then(function (responseText) {
          var doc = new DOMParser().parseFromString(responseText, 'text/html');
          var productEl = doc.querySelector('product-info');
          modal.preprocessHTML(productEl);
          HTMLUpdateUtility.setInnerHTML(modal.modalContent, productEl.outerHTML);
          if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
          parentProto.show.call(modal, opener);
        })
        .catch(function () {
          cache.delete(url);
          _orig.call(modal, opener);
        })
        .finally(function () {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    };
  });
})();
